import type {
  AccessRequestOf,
  ArgsOf,
  RequestOf,
  StagedAccessRequestOf,
  StagedRequestOf,
} from "../../../access/types.ts";
import { Access } from "../../../access/types.ts";
import { GcloudServices } from "./resource/constants.ts";

const projectArg = {
  description: "Project ID",
  label: "Project ID",
  type: "static",
  optional: true,
  isPreselect: true,
  waitForInput: true,
} as const;

const projectIdArg = {
  type: "generated",
  label: "Project ID",
} as const;

const roleNameArg = {
  description: "Role name or identifier",
  label: "Role",
  type: "dynamic",
  multivalued: true,
} as const;

const principalTypeArg = {
  description: "The type of principal that should receive access",
  label: "Principal type",
  type: "input",
  choices: ["user", "group", "service-account", "wif-identity", "wif-user"],
  supportedCommands: ["grant", "request"],
  optional: true,
} as const;

export const GcloudAccesses = {
  resource: Access({
    description: "Resource within Google Cloud",
    label: "Resource",
    maturity: "ga",
    delegation: {},
    generated: {
      roles: {
        type: "object",
        multivalued: true,
        properties: {
          role: { type: "string" },
          permissionId: { type: "string" },
        },
      },
    },
    parameters: {
      project: projectArg,
      projectId: projectIdArg,
      "principal-type": principalTypeArg,
      locator: {
        type: "dynamic",
        label: "Resource locator",
        description: "Resource Locator",
        hint: "Supported resources: Compute instances & zones, Storage buckets, BigQuery tables, service accounts, Secrets Manager secrets",
      },
      accesses: {
        type: "dynamic",
        label: "Access",
        description: "admin, read, write or role name",
        multivalued: true,
      },
      serviceAccount: {
        type: "input",
        label: "Service account",
        description: "Service Account",
        hint: "Find with: gcloud compute instances describe INSTANCE-NAME | grep serviceAccounts -A 1",
        optional: true,
      },
      object: {
        type: "input",
        label: "File or folder",
        description: "Path to file or folder inside bucket",
        optional: true,
      },
      service: {
        type: "input",
        label: "Service",
        description: "Service",
        hint: "Supported services: compute, storage, bigquery, iam, secretmanager",
        choices: GcloudServices,
        optional: true,
      },
      resourceTag: {
        type: "object",
        label: "Resource tag",
        optional: true,
        properties: {
          tagKey: {
            type: "generated",
            label: "Tag key",
            optional: true,
          },
          tagValue: {
            type: "generated",
            label: "Tag value",
            optional: true,
          },
        },
      },
      permissionList: {
        type: "object",
        label: "Permission list",
        multivalued: true,
      },
    },
    render: { locator: "projectId", policy: "accesses" } as const,
  }),
  role: Access({
    description: "Role within Google Cloud",
    label: "Role",
    maturity: "ga",
    delegation: {},
    generated: {},
    parameters: {
      project: projectArg,
      projectId: projectIdArg,
      "principal-type": principalTypeArg,
      name: roleNameArg,
      role: {
        type: "generated",
        label: "Role ID",
      },
      nIncludedPermissions: {
        type: "generated",
        label: "Number of included permissions",
        optional: true,
      },
    },
    render: { locator: "projectId", policy: "role" } as const,
  }),
  permission: Access({
    description: "Permission within Google Cloud",
    label: "Permission",
    maturity: "ga",
    delegation: {},
    generated: {
      role: {
        type: "string",
      },
    },
    parameters: {
      project: projectArg,
      projectId: projectIdArg,
      "principal-type": principalTypeArg,
      name: {
        description: "Permission name or identifier",
        label: "Permissions",
        type: "dynamic",
        multivalued: true,
      },
    },
    render: { locator: "projectId", policy: "name" } as const,
  }),
};

export type GcloudAccesses = typeof GcloudAccesses;
export type GcloudAccessType = keyof GcloudAccesses;
export type GcloudAccess = GcloudAccesses[GcloudAccessType];
export type GcloudAccessArgs = ArgsOf<GcloudAccess>;

export type GcloudResourceAccess = GcloudAccesses["resource"];
export type GcloudResourceAccessArgs = ArgsOf<GcloudResourceAccess>;
export type GcloudResourceRequest = RequestOf<GcloudResourceAccess>;
export type GcloudResourceStagedRequest = StagedRequestOf<GcloudResourceAccess>;

export type GcloudPermissionAccess = GcloudAccesses["permission"];
export type GcloudPermissionAccessArgs = ArgsOf<GcloudPermissionAccess>;
export type GcloudPermissionRequest = RequestOf<GcloudPermissionAccess>;
export type GcloudPermissionStagedRequest =
  StagedRequestOf<GcloudPermissionAccess>;

export type GcloudRoleAccess = GcloudAccesses["role"];
export type GcloudRoleAccessArgs = ArgsOf<GcloudRoleAccess>;
export type GcloudRoleRequest = RequestOf<GcloudRoleAccess>;
export type GcloudRoleStagedRequest = StagedRequestOf<GcloudRoleAccess>;

export type GcloudRequest = RequestOf<GcloudAccess>;
export type GcloudStagedRequest = StagedRequestOf<GcloudAccess>;

export type GcloudAccessRequest = AccessRequestOf<GcloudAccesses>;
export type StagedGcloudAccessRequest = StagedAccessRequestOf<GcloudAccesses>;

export type GcloudPrincipalType = GcloudAccessArgs["principal-type"];
